/**
 * Formatage texte des fiches pages de référence (partagé export CSV + rapport JSON).
 */

function section(title, body) {
  const text = String(body || '').trim();
  if (!text) return '';
  return `=== ${title} ===\n${text}`;
}

export function formatReferencePageCriteria(entry) {
  const c = entry.seoContent?.criteria;
  if (!c) return '';
  const hs = entry.seoContent?.headingsStructure;
  const parts = [
    `Title (${c.titleLen} car.) : ${c.titleOk ? 'OK' : 'À corriger'} — ${entry.seoContent.titleStatus?.label || ''}${entry.seoContent.titleStatus?.recommendation ? ` — ${entry.seoContent.titleStatus.recommendation}` : ''}`,
    `Meta (${c.metaLen} car.) : ${c.metaOk ? 'OK' : 'À corriger'} — ${entry.seoContent.metaStatus?.label || ''}${entry.seoContent.metaStatus?.recommendation ? ` — ${entry.seoContent.metaStatus.recommendation}` : ''}`,
    `H1 (${c.h1Count}) : ${c.h1Ok ? 'OK' : 'Manquant ou incorrect'}${entry.seoContent.h1 ? ` — « ${entry.seoContent.h1} »` : ''}`,
    `Images : ${c.nbImages} total, ${c.nbWithoutAlt} sans alt — ${c.imagesOk ? 'OK' : 'À corriger'}`,
    `Canonical : ${c.canonOk ? 'OK' : 'À vérifier'}${entry.seoContent.canonicalUrl ? ` — ${entry.seoContent.canonicalUrl}` : ''}`,
    hs?.label ? `Structure titres : ${hs.label} (${JSON.stringify(hs.counts || {})})` : '',
  ];
  return parts.filter(Boolean).join('\n');
}

export function formatReferencePageHeadingsAnalysis(entry) {
  const ha = entry.seoContent?.headingsAnalysis;
  if (!ha) return '';
  const lines = [];
  for (const s of ha.strengths || []) lines.push(`+ ${s}`);
  for (const i of ha.issues || []) {
    lines.push(`[${i.severity}] ${i.message}${i.fix ? `\n→ ${i.fix}` : ''}`);
  }
  for (const r of ha.contentRecommendations || []) {
    lines.push(`Conseil : ${r.message}${r.suggestion ? `\n→ ${r.suggestion}` : ''}`);
  }
  return lines.join('\n');
}

export function formatReferencePageHeadings(entry) {
  const list = entry.seoContent?.headings || [];
  if (!list.length) return '';
  return list.map((h) => `H${h.level} : ${h.text}`).join('\n');
}

export function formatReferencePageImages(entry) {
  const images = entry.seoContent?.images || [];
  const sum = entry.seoContent?.imagesSummary;
  if (!images.length && !sum) return '';
  const lines = [`Total : ${sum?.total ?? images.length}, sans alt : ${sum?.withoutAlt ?? '—'}`];
  for (const img of images) {
    lines.push(`- ${img.src}${img.hasAlt ? ` (alt: ${img.alt || 'vide'})` : ' (SANS ALT)'}`);
  }
  return lines.join('\n');
}

export function formatReferencePageTechnicalErrors(entry) {
  return (entry.technicalErrors || [])
    .map((e) => `[${e.severity}] ${e.category} — ${e.message}\nPourquoi : ${e.why || '—'}\nAction : ${e.action || '—'} (effort: ${e.effort})`)
    .join('\n\n');
}

export function formatReferencePageFindings(entry) {
  return (entry.findings || [])
    .map((f) => `[${f.severity}] [${f.pilier}] ${f.title}\nPourquoi : ${f.why}\nAction : ${f.action}${f.effort ? ` (effort: ${f.effort})` : ''}`)
    .join('\n\n');
}

export function formatReferencePageSpeedScores(ps) {
  if (!ps?.scores) return '';
  const s = ps.scores;
  return [
    `Moyenne : ${s.average ?? '—'}`,
    `Desktop : ${s.desktop ?? '—'}`,
    `Mobile : ${s.mobile ?? '—'}`,
    `Accessibilité : ${s.accessibility ?? '—'}`,
    `Bonnes pratiques : ${s.bestPractices ?? '—'}`,
    s.seo != null ? `SEO Lighthouse : ${s.seo}` : null,
  ].filter(Boolean).join('\n');
}

export function formatReferencePageVitals(ps) {
  return (ps?.vitals || [])
    .map((v) => {
      const lines = [`${v.label}${v.displayValue ? ` : ${v.displayValue}` : ''}${v.priority ? ` (${v.priority})` : ''}`];
      if (v.definition) lines.push(v.definition);
      if (v.impactWhy) lines.push(`Impact : ${v.impactWhy}`);
      if (v.contextualActions?.length) {
        lines.push(`Actions liées : ${v.contextualActions.join(' | ')}`);
      }
      return lines.join('\n');
    })
    .join('\n\n');
}

export function formatReferencePageOptimizations(ps) {
  return (ps?.optimizations || [])
    .map((o) => {
      const lines = [
        `[${o.priority || '—'}] [${o.effort}] ${o.label}${o.displayValue ? ` (${o.displayValue})` : ''}`,
      ];
      if (o.why) lines.push(`Pourquoi : ${o.why}`);
      if (o.action) lines.push(`Correction : ${o.action}`);
      if (o.affectedResources) lines.push(`Éléments : ${o.affectedResources}`);
      if (o.deviceScope && o.deviceScope !== 'both') lines.push(`Périmètre : ${o.deviceScope}`);
      return lines.join('\n');
    })
    .join('\n\n');
}

export function formatReferencePageActionPlan(entry) {
  const plan = entry.actionPlan;
  if (!plan?.todos?.length && !plan?.summary) return '';
  const lines = [];
  if (plan.summary) lines.push(`Résumé plan : ${plan.summary}`);
  if (plan.stale) lines.push('⚠ Données d’audit plus récentes que le plan');
  for (const t of plan.todos || []) {
    lines.push(`\n[${t.priority || '—'}] ${t.title}${t.category ? ` (${t.category})` : ''}`);
    if (t.why) lines.push(`Pourquoi : ${t.why}`);
    if (t.description) lines.push(t.description);
    if (t.effort) lines.push(`Effort : ${t.effort}`);
    for (const step of t.steps || []) {
      if (step.cible) lines.push(`  • Cible : ${step.cible}`);
      if (step.modification) lines.push(`    → ${step.modification}`);
    }
  }
  return lines.join('\n').trim();
}

/** Bloc texte unique pour coller dans une IA. */
export function buildReferencePageFullNarrative(entry, projectName = '') {
  const ps = entry.pageSpeed;
  const blocks = [
    projectName ? `Projet : ${projectName}` : '',
    section('URL', entry.url),
    section('Verdict', entry.verdict?.text),
    section('Scores', `SEO : ${entry.scores?.seo ?? '—'} (${entry.scores?.labels?.seo || '—'}) | Performance : ${entry.scores?.performance ?? '—'} (${entry.scores?.labels?.performance || '—'})`),
    section('Contenu SEO', `Titre : ${entry.seoContent?.title || '—'}\nMeta : ${entry.seoContent?.metaDescription || '—'}\nH1 : ${entry.seoContent?.h1 || '—'}\nCanonique : ${entry.seoContent?.canonicalUrl || '—'}`),
    section('Critères on-page', formatReferencePageCriteria(entry)),
    section('Structure des titres (Hn)', formatReferencePageHeadings(entry)),
    section('Analyse structure titres', formatReferencePageHeadingsAnalysis(entry)),
    section('Images', formatReferencePageImages(entry)),
    section('Erreurs techniques', formatReferencePageTechnicalErrors(entry)),
    section('Constats et recommandations', formatReferencePageFindings(entry)),
    section('Synthèse PageSpeed (IA)', ps?.pagespeedSummaryIa || ps?.summary || ''),
    section('Scores PageSpeed', formatReferencePageSpeedScores(ps)),
    ps?.error ? section('Erreur PageSpeed', ps.error) : '',
    section('Métriques vitales', formatReferencePageVitals(ps)),
    section('Audits PageSpeed / optimisations', formatReferencePageOptimizations(ps)),
    section('Plan d\'action IA', formatReferencePageActionPlan(entry)),
  ];
  return blocks.filter(Boolean).join('\n\n');
}

/** Champs texte formatés (colonnes CSV) + narrative, pour export JSON. */
export function buildEnrichedReferencePageReport(entry, projectName = '') {
  const ps = entry.pageSpeed;
  return {
    ...entry,
    formatted: {
      criteria: formatReferencePageCriteria(entry),
      headings: formatReferencePageHeadings(entry),
      headingsAnalysis: formatReferencePageHeadingsAnalysis(entry),
      images: formatReferencePageImages(entry),
      technicalErrors: formatReferencePageTechnicalErrors(entry),
      findings: formatReferencePageFindings(entry),
      pageSpeedSummary: ps?.summary ?? '',
      pageSpeedSummaryIa: ps?.pagespeedSummaryIa ?? '',
      pageSpeedError: ps?.error ?? '',
      pageSpeedScores: formatReferencePageSpeedScores(ps),
      vitals: formatReferencePageVitals(ps),
      optimizations: formatReferencePageOptimizations(ps),
      actionPlan: formatReferencePageActionPlan(entry),
    },
    fullNarrativeForAi: buildReferencePageFullNarrative(entry, projectName),
  };
}

export const REFERENCE_PAGE_CSV_HEADERS = [
  'URL',
  'Verdict',
  'Score SEO',
  'Libellé SEO',
  'Score performance',
  'Libellé performance',
  'Titre',
  'Meta description',
  'H1',
  'URL canonique',
  'Critères SEO',
  'Structure des titres (Hn)',
  'Analyse structure titres',
  'Images (liste complète)',
  'Erreurs techniques',
  'Constats et recommandations',
  'Synthèse PageSpeed',
  'Synthèse PageSpeed (IA)',
  'Erreur PageSpeed',
  'Scores PageSpeed',
  'Métriques vitales',
  'Audits PageSpeed détaillés',
  'Plan d\'action IA',
  'Contenu complet (pour IA)',
];

export function referencePageEntryToCsvRow(entry, projectName = '') {
  const ps = entry.pageSpeed;
  const f = buildEnrichedReferencePageReport(entry, projectName).formatted;
  return [
    entry.url,
    entry.verdict?.text || '',
    entry.scores?.seo ?? '',
    entry.scores?.labels?.seo ?? '',
    entry.scores?.performance ?? '',
    entry.scores?.labels?.performance ?? '',
    entry.seoContent?.title ?? '',
    entry.seoContent?.metaDescription ?? '',
    entry.seoContent?.h1 ?? '',
    entry.seoContent?.canonicalUrl ?? '',
    f.criteria,
    f.headings,
    f.headingsAnalysis,
    f.images,
    f.technicalErrors,
    f.findings,
    f.pageSpeedSummary,
    f.pageSpeedSummaryIa,
    f.pageSpeedError,
    f.pageSpeedScores,
    f.vitals,
    f.optimizations,
    f.actionPlan,
    buildReferencePageFullNarrative(entry, projectName),
  ];
}
